import { inject } from '@angular/core';

import { CanActivateFn, Router } from '@angular/router';
import { ElementService } from '@services/element.service';
import { MenuService } from '@services/menu.service';
import { PermissionsService } from '@services/permissions.service';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';
import { AppStateService } from '../layout/app-state.service';
import { getElementInfoByProp } from '../models/element';

export const UserPermissionsGuard: CanActivateFn = (_, state) => {
  const router = inject(Router);
  const appStateService = inject(AppStateService);
  const menuService = inject(MenuService);
  const elementService = inject(ElementService);
  const permissionService = inject(PermissionsService);

  const elementName = state.url.split('/')[1]?.toLowerCase();
  const elementInfo = getElementInfoByProp('url', elementName);

  const userElements$ = appStateService.userElements()
    ? of(appStateService.userElements())
    : appStateService.getUserObservable().pipe(
        filter(Boolean),
        take(1),
        switchMap(userObj => elementService.getElementsForUser(userObj.userId)),
        tap(elements => {
          if (elements?.length) {
            appStateService.setUserElements(elements);
          }
        })
      );

  return userElements$.pipe(
    switchMap(userElements => {
      if (!userElements || !userElements.some(e => e.url?.toLowerCase() === `/${elementName}`)) {
        router.navigate(['/error'], { queryParams: { errorCode: 403 } });
        return of(false);
      }

      let elementId = appStateService.element()?.elementId;
      if (!elementId && elementInfo?.elementId) {
        appStateService.setElement(elementInfo, false);
        elementId = elementInfo.elementId;
      }

      const pagePath = state.url.split('/')[2];

      return elementService.checkElementMenuNavigationPermission(elementId, pagePath).pipe(
        switchMap(() =>
          menuService.getMenuSecuityGroupPerPage(elementId, pagePath).pipe(
            tap(menuSecurity => permissionService.setPermissionsPerPage(menuSecurity)),
            map(() => true)
          )
        ),
        catchError(err => {
          router.navigate(['/error'], { queryParams: { errorCode: err?.status || 403 } });
          return of(false);
        })
      );
    }),
    catchError(err => {
      router.navigate(['/error'], { queryParams: { errorCode: err?.status || 403 } });
      return of(false);
    })
  );
};
